import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["allOutputsByActionTypeId", "currentlyStagedFlow", "enrollmentGenerationResponse", "flow", "flowId", "history", "instanceOutputsByActionId", "requestFlowValidationObject", "updateFlowLocally", "zoomLevel"];
import { useEffect, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { generatePath, withRouter, useRouteMatch } from 'react-router-dom';
import { compose } from 'redux';
import memoize from 'transmute/memoize';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIIcon from 'UIComponents/icon/UIIcon';
import { wireActionOutputs } from 'automation-ui-action-features/queries/actionOutputQueries';
import * as flowActions from 'automation-ui-canvas/actions/FlowActions';
import { updateFlowLocally as _updateFlowLocally } from 'automation-ui-canvas/actions/FlowActions';
import FlowCanvasNode from 'automation-ui-canvas/components/canvas/FlowCanvasNode';
import * as AbsolutePaths from 'automation-ui-canvas/constants/AbsolutePaths';
import { getActionOutputsForTargetActionById } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import { useGetFlowById, wireFlowById, wireUpdateFlow } from 'automation-ui-canvas/queries/flowQueries';
import { getEnrollmentGenerationResponse } from 'automation-ui-canvas/selectors/aiAssistantSelectors';
import { getFlowValidation } from 'automation-ui-canvas/selectors/flowSelectors';
import { parseLocation, joinPathAndQuery } from 'automation-ui-routing/utils';
import { revertStagedChanges } from 'automation-ui-canvas/actions/FlowActions';
import ModeIndicator from 'automation-ui-canvas/containers/enrollment-history/utils/ModeIndicator';
import * as CanvasIdentifiers from 'automation-ui-flow-core/canvas/CanvasIdentifiers';
import { getZoomLevel } from 'automation-ui-canvas/selectors/localStorageSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FlowCanvasAiAssistantReviewDecorator = WrappedComponent => {
  const FlowCanvasAiAssistantReviewContainer = _ref => {
    let {
        allOutputsByActionTypeId,
        currentlyStagedFlow,
        enrollmentGenerationResponse,
        flow,
        flowId,
        history,
        instanceOutputsByActionId,
        requestFlowValidationObject,
        updateFlowLocally,
        zoomLevel
      } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, _excluded);
    const isReviewingTriggers = !!enrollmentGenerationResponse;
    const isFirstRender = useRef(true);
    const {
      data: flowByIdData
    } = useGetFlowById(flowId);
    const fetchedFlow = flowByIdData === null || flowByIdData === void 0 ? void 0 : flowByIdData.flowById;
    const {
      params: {
        assetType
      }
    } = useRouteMatch();
    const dispatch = useDispatch();
    const edgeId = parseLocation(history.location).edgeId;
    useEffect(() => {
      // handles redirecting to AI-Assistant panel in case of direct routing to this preview mode (should not be allowed)
      if (isFirstRender.current && !currentlyStagedFlow) {
        history.push(generatePath(AbsolutePaths.AI_ASSISTANT, {
          flowId,
          assetType
        }));
      }
      isFirstRender.current = false;
    }, [currentlyStagedFlow, flowId, history, assetType]);
    useEffect(() => {
      return () => {
        // revert local flow back to fetched flow when dismounting
        if (fetchedFlow) {
          updateFlowLocally(fetchedFlow);
        }
      };
    }, [fetchedFlow, updateFlowLocally]);
    useEffect(() => {
      requestFlowValidationObject(flow);
    }, [flow, requestFlowValidationObject]);
    const handleGetActionOutputsForTargetActionById = (targetActionId, isNew) => getActionOutputsForTargetActionById({
      flow,
      allOutputsByActionTypeId,
      instanceOutputsByActionId,
      targetActionId,
      isNewAction: isNew
    });
    const onRenderCanvasNode = memoize(({
      id,
      width,
      height
    }) => {
      const disabledDescription = /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: isReviewingTriggers ? 'automation.aiAssistantGenerateCanvas.cardTooltip.trigger_jsx' : 'automation.aiAssistantGenerateCanvas.cardTooltip.actions_jsx',
        elements: {
          Bold: 'strong'
        }
      });
      let isHighlighted = false;
      if (id === CanvasIdentifiers.FIRST_NODE) {
        isHighlighted = !!enrollmentGenerationResponse;
      } else {
        isHighlighted = fetchedFlow ? !fetchedFlow.getActionById(id) : false;
      }
      return /*#__PURE__*/_jsx(FlowCanvasNode, {
        id: id,
        width: width,
        height: height,
        flow: flow,
        isHighlighted: isHighlighted,
        isDisabled: true,
        readOnly: true,
        getActionOutputsForTargetActionById: handleGetActionOutputsForTargetActionById,
        disabledDescription: disabledDescription,
        zoomLevel: zoomLevel
      });
    }, ({
      id: nodeId
    }) => {
      return `${nodeId}-${flowId}-${zoomLevel}`;
    });
    if (!currentlyStagedFlow) {
      return null;
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ModeIndicator, {
        use: "preview",
        onCancel: () => {
          dispatch(revertStagedChanges());
          history.push(joinPathAndQuery(generatePath(AbsolutePaths.AI_ASSISTANT, {
            flowId,
            assetType
          }), {
            edgeId
          }));
        },
        modeTitle: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(UIIcon, {
            name: "artificialIntelligence",
            size: "xxs",
            className: "m-right-2"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: isReviewingTriggers ? 'aiAssistantReview.triggers.title' : 'aiAssistantReview.actions.title'
          })]
        })
      }), /*#__PURE__*/_jsx(WrappedComponent, Object.assign({
        flow: currentlyStagedFlow,
        onRenderCanvasNode: onRenderCanvasNode,
        goToEditNodeId: null
        // TODO - investigate editing actions while in preview mode
        ,
        readOnlyOverride: true,
        requestFlowValidationObject: requestFlowValidationObject,
        zoomLevel: zoomLevel
      }, props))]
    });
  };
  return compose(wireActionOutputs, withRouter, _WrappedComponent => props => /*#__PURE__*/_jsx(_WrappedComponent, Object.assign({}, props, {
    flowId: Number(props.match.params.flowId)
  })), wireFlowById, wireUpdateFlow, connect((state, {
    flowId
  }) => {
    const enrollmentGenerationResponse = getEnrollmentGenerationResponse(state);
    const flowValidation = getFlowValidation(flowId)(state);
    return {
      enrollmentGenerationResponse,
      flowValidation,
      zoomLevel: getZoomLevel(flowId)(state)
    };
  }, {
    requestFlowValidationObject: flowActions.requestFlowValidationObject,
    updateFlowLocally: _updateFlowLocally
  }))(FlowCanvasAiAssistantReviewContainer);
};
export default FlowCanvasAiAssistantReviewDecorator;